import { reaction } from 'mobx';

import { apiStore } from 'stores/global/ApiStore';
import { API_ERROR_TYPE } from 'types/ApiError';

import { AppConfigStore } from './AppConfigStore';
import { GeneralInformationStrapi } from './GeneralInformationStrapi/GeneralInformationStrapi';
import { LeadDrawerStore } from './LeadDrawerStore';
import { MeetingDrawerStore } from './MeetingDrawerStore';
import { NotificationsStore } from './NotificationsStore';
import { PlaceTypeHandbookStore } from './PlaceTypeHandbookStore';
import { ProjectHandbookStore } from './ProjectHandbookStore';
import { PushSubscribeStore } from './PushSubscribeStore';
import { QueryParamsStore } from './QueryParamsStore';
import { TeamResponsibleStore } from './TeamResponsibleStore';
import { UserNotificationsStore } from './UserNotificationsStore';
import { UserStore } from './UserStore';
import { IRootStore } from './types';

export class RootStore implements IRootStore {
  readonly notificationsStore = new NotificationsStore();
  readonly queryParamsStore = new QueryParamsStore();
  readonly userStore = new UserStore({
    notificationsStore: this.notificationsStore,
  });

  readonly placeTypeHandbookStore = new PlaceTypeHandbookStore({
    notificationStore: this.notificationsStore,
  });

  readonly projectsHandbookStore = new ProjectHandbookStore({
    notificationStore: this.notificationsStore,
    placeTypeHandbookStore: this.placeTypeHandbookStore,
  });

  readonly pushSubscribeStore = new PushSubscribeStore({ userStore: this.userStore });

  readonly userNotificationsStore = new UserNotificationsStore({
    userStore: this.userStore,
    notificationStore: this.notificationsStore,
  });

  readonly leadDrawer = new LeadDrawerStore({
    notificationStore: this.notificationsStore,
  });

  readonly meetingDrawer = new MeetingDrawerStore({
    notificationStore: this.notificationsStore,
  });

  readonly generalInformationStrapi = new GeneralInformationStrapi();

  readonly appConfigStore = new AppConfigStore();

  readonly teamResponsibleStore = new TeamResponsibleStore();

  constructor() {
    this._registerNotAccreditedInterceptor();

    reaction(
      () => Boolean(this.userStore.user),
      (isAuth) => {
        if (isAuth) {
          this.placeTypeHandbookStore.load();
          this.projectsHandbookStore.load();
          this.generalInformationStrapi.fetch();
          this.teamResponsibleStore.load();
        }
      },
    );
  }

  private _registerNotAccreditedInterceptor(): void {
    const axiosInstance = apiStore.createRequest().axiosInstance;

    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.data?.type === API_ERROR_TYPE.notAccredited) {
          this.userStore.handleNotAccreditedError();
        }

        return Promise.reject(error);
      },
    );
  }
}
